// A single ordered record of everything that happens on the voice path: the
// handshake steps, the peer connection's state changes, every event OpenAI
// pushes down the data channel, and the local audio milestones.
//
// Deliberately outside zustand. Entries arrive from callbacks that fire dozens
// of times a second during a reply, and routing those through reactive state
// would re-render the exercise page along with them. The panel subscribes to
// this directly instead.

const ENTRY_LIMIT = 300;

let entries = [];
let sequence = 0;
const listeners = new Set();

const notify = () => listeners.forEach(listener => listener());

const publishToWindow = () => {
  if (typeof window !== 'undefined') {
    // Full detail for the console; the panel only renders the summaries.
    window.__realtimeTimeline = entries;
  }
};

/**
 * Turns a raw OpenAI event into one readable line. The type alone is not
 * enough — "what did it actually say" and "why did it stop" both live in the
 * payload, and that is the whole point of watching the feed.
 */
export function summarizeRealtimeEvent(event) {
  const type = event?.type ?? '(untyped)';

  switch (type) {
    case 'session.created':
    case 'session.updated':
      return `model=${event.session?.model ?? '?'} voice=${
        event.session?.audio?.output?.voice ?? event.session?.voice ?? '?'
      }`;

    case 'response.created':
      return `response ${event.response?.id ?? '?'} started`;

    case 'response.output_audio_transcript.delta':
    case 'response.audio_transcript.delta':
    case 'response.output_text.delta':
      return JSON.stringify(event.delta ?? '');

    case 'response.output_audio_transcript.done':
    case 'response.audio_transcript.done':
    case 'response.output_text.done':
      return JSON.stringify(event.transcript ?? event.text ?? '');

    case 'output_audio_buffer.started':
      return 'agent started speaking';

    case 'output_audio_buffer.stopped':
      return 'agent stopped speaking';

    case 'output_audio_buffer.cleared':
      return 'audio buffer cleared (interrupted)';

    case 'response.done': {
      const status = event.response?.status ?? '?';
      const reason = event.response?.status_details?.reason;
      const tokens = event.response?.usage?.output_tokens;
      return `${status}${reason ? ` (${reason})` : ''}${
        tokens === undefined ? '' : ` · ${tokens} output tokens`
      }`;
    }

    case 'conversation.item.created':
      return `item ${event.item?.role ?? '?'} ${event.item?.type ?? ''}`.trim();

    case 'rate_limits.updated':
      return (event.rate_limits ?? []).map(limit => `${limit.name}:${limit.remaining}`).join(' ');

    case 'error':
      return `${event.error?.code ?? '?'} — ${event.error?.message ?? 'unknown error'}`;

    default: {
      const raw = JSON.stringify(event ?? {});
      return raw.length > 120 ? `${raw.slice(0, 120)}…` : raw;
    }
  }
}

/**
 * `source` groups the line by where it came from, which is what makes the feed
 * readable: `openai` is the remote agent, `webrtc` the transport, `audio` local
 * playback, `api` our own backend calls.
 */
export function logRealtimeEvent({ source = 'system', type, summary = '', detail = null }) {
  sequence += 1;

  const entry = {
    id: sequence,
    at: Date.now(),
    source,
    type: type ?? 'event',
    summary,
    detail,
  };

  entries = entries.length >= ENTRY_LIMIT ? [...entries.slice(1), entry] : [...entries, entry];
  publishToWindow();
  notify();

  return entry;
}

export function logOpenAiEvent(event) {
  return logRealtimeEvent({
    source: event?.type === 'error' ? 'error' : 'openai',
    type: event?.type ?? '(untyped)',
    summary: summarizeRealtimeEvent(event),
    detail: event,
  });
}

export function getRealtimeTimeline() {
  return entries;
}

export function subscribeRealtimeTimeline(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function clearRealtimeTimeline() {
  entries = [];
  publishToWindow();
  notify();
}
